import React from 'react';
import PropTypes from 'prop-types';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Paper from '@mui/material/Paper';
import InboxIcon from '@mui/icons-material/Inbox';
import SearchOffIcon from '@mui/icons-material/SearchOff';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import FolderOpenIcon from '@mui/icons-material/FolderOpen';
import AddIcon from '@mui/icons-material/Add';

/**
 * Empty State Component
 * Displays a friendly message when there's no data to show
 * 
 * @param {string} type - Type of empty state: 'no-data', 'no-results', 'error', 'empty-folder'
 * @param {string} title - Custom title (optional)
 * @param {string} message - Custom message (optional)
 * @param {React.Node} icon - Custom icon (optional)
 * @param {Object} action - Action button config: { label, onClick, icon }
 * @param {boolean} variant - Display variant: 'default', 'minimal'
 * 
 * Usage Examples:
 * 
 * // No data
 * <EmptyState type="no-data" />
 * 
 * // No search results
 * <EmptyState 
 *   type="no-results" 
 *   message="Tiada rekod ditemui untuk 'Ahmad'"
 * />
 * 
 * // Custom empty state with action
 * <EmptyState
 *   title="Tiada Akaun"
 *   message="Belum ada akaun ditambah"
 *   icon={<PersonIcon />}
 *   action={{
 *     label: "Tambah Akaun",
 *     onClick: handleAddAccount,
 *     icon: <AddIcon />
 *   }}
 * />
 */
const EmptyState = ({
  type = 'no-data',
  title,
  message,
  icon,
  action,
  variant = 'default',
  sx = {},
}) => {
  // Default configurations for each type
  const configs = {
    'no-data': {
      icon: <InboxIcon />,
      title: 'Tiada Data',
      message: 'Tiada rekod untuk dipaparkan.',
    },
    'no-results': {
      icon: <SearchOffIcon />,
      title: 'Tiada Hasil',
      message: 'Tiada rekod ditemui. Cuba ubah carian anda.',
    },
    'error': {
      icon: <ErrorOutlineIcon />,
      title: 'Ralat',
      message: 'Terdapat masalah memuatkan data. Sila cuba lagi.',
    },
    'empty-folder': {
      icon: <FolderOpenIcon />,
      title: 'Folder Kosong',
      message: 'Folder ini masih kosong.',
    },
  };

  const config = configs[type] || configs['no-data'];
  const displayTitle = title || config.title;
  const displayMessage = message || config.message;
  const displayIcon = icon || config.icon;

  if (variant === 'minimal') {
    // Minimal variant - just text, no paper container
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          padding: 4,
          textAlign: 'center',
          ...sx,
        }}
      >
        <Box
          sx={{
            fontSize: 48,
            color: 'text.disabled',
            marginBottom: 1,
          }}
        >
          {displayIcon}
        </Box>
        <Typography variant="body2" color="text.secondary">
          {displayMessage}
        </Typography>
      </Box>
    );
  }

  // Default variant - full featured with paper container
  return (
    <Paper
      elevation={0}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 6,
        textAlign: 'center',
        backgroundColor: 'background.default',
        border: '2px dashed',
        borderColor: 'divider',
        borderRadius: 2,
        minHeight: 300,
        ...sx,
      }}
    >
      <Box
        sx={{
          fontSize: 80,
          color: 'text.disabled',
          marginBottom: 2,
          opacity: 0.5,
        }}
      >
        {displayIcon}
      </Box>

      <Typography
        variant="h6"
        color="text.primary"
        gutterBottom
        sx={{ fontWeight: 600 }}
      >
        {displayTitle}
      </Typography>

      <Typography
        variant="body1"
        color="text.secondary"
        sx={{ marginBottom: 3, maxWidth: 400 }}
      >
        {displayMessage}
      </Typography>

      {action && (
        <Button
          variant="contained"
          color="primary"
          size="large"
          onClick={action.onClick}
          startIcon={action.icon || <AddIcon />}
          sx={{ marginTop: 2 }}
        >
          {action.label}
        </Button>
      )}
    </Paper>
  );
};

/**
 * Convenience components for common empty states
 */

// No data empty state
export const NoData = (props) => (
  <EmptyState type="no-data" {...props} />
);

// No search results empty state
export const NoResults = (props) => (
  <EmptyState type="no-results" {...props} />
);

// Error empty state
export const ErrorState = (props) => (
  <EmptyState type="error" {...props} />
);

// Empty folder state
export const EmptyFolder = (props) => (
  <EmptyState type="empty-folder" {...props} />
);

/**
 * Conditional wrapper - shows children if data exists, otherwise shows empty state
 * 
 * Usage:
 * <ConditionalEmptyState 
 *   data={accounts} 
 *   emptyProps={{ 
 *     type: 'no-data',
 *     message: 'Tiada akaun dijumpai'
 *   }}
 * >
 *   <AccountList accounts={accounts} />
 * </ConditionalEmptyState>
 */
export const ConditionalEmptyState = ({ data, children, emptyProps = {} }) => {
  const isEmpty = !data || (Array.isArray(data) && data.length === 0);

  if (isEmpty) {
    return <EmptyState {...emptyProps} />;
  }

  return <>{children}</>;
};

// PropTypes validation
EmptyState.propTypes = {
  type: PropTypes.oneOf(['no-data', 'no-results', 'error', 'empty-folder']),
  title: PropTypes.string,
  message: PropTypes.string,
  icon: PropTypes.node,
  action: PropTypes.shape({
    label: PropTypes.string.isRequired,
    onClick: PropTypes.func.isRequired,
    icon: PropTypes.node,
  }),
  variant: PropTypes.oneOf(['default', 'minimal']),
  sx: PropTypes.object,
};

ConditionalEmptyState.propTypes = {
  data: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
  children: PropTypes.node.isRequired,
  emptyProps: PropTypes.object,
};

export default EmptyState;
